/**
 * №29 (S1) — the work rail's PLAN tab. Mockup A's "THIS RUN", mockup D's
 * "Disclosure review · 12 steps": the steps of the work in front of you, which one is
 * waiting, and the one thing to do about it.
 *
 * **Every step here is a real step of a real workflow instance.** The source is the
 * shipped №16.2.1 projection — `workflow_instances_for` → `workflow_instance_detail`
 * → `workflow_whats_next` — read through the same hooks the process surfaces use
 * (R1: the rail is a new VIEW of the spine, not a second copy of it). The probe's
 * rule, verbatim: *"every chip is a real record, never decoration"*. So there is no
 * synthesised plan for work that has none: a task with no declared process, or an
 * anchor with no instance, says exactly that.
 *
 * ## Which step is NOW is the ENGINE's answer, not a guess (S1 fix-forward)
 *
 * The first pass derived "now" by matching the focused task's TITLE against a step
 * name — a guess, and one that silently produced NO current step whenever the titles
 * differed, which is most of the time. David read the result exactly as it was drawn:
 * *"it looks like the previous completed item is the active item."* He was right, and
 * the fault was upstream of the styling.
 *
 * `workflow_whats_next` already returns `ready[]` (every step the engine will accept
 * work on right now) and `nextAction` (the first actionable one, with its own verb
 * and its reason). Those are authoritative. So: **ready ⇒ now**, and the lead among
 * them carries the action. Nothing is inferred from a string any more.
 *
 * ## The action is the shipped one, not a new one
 *
 * The DO-NEXT affordance already exists and is already ruled — `WorkflowPanel`'s
 * do-next card: ONE primary button, labelled with the step's own `verb`, calling
 * `workflow_advance`; a DECISION instead links to where its outcomes live, because a
 * decision cannot be taken in one click. The rail reuses that grammar exactly, so the
 * rail and the process panel can never disagree about what "do next" means or what
 * pressing it does.
 *
 * D-RN2 (№16.2.1): render `state`, never compare `status`.
 */
import { Link } from "react-router";
import { useAnchorWorkflows, useInstanceDetail, useWhatsNext, useWorkflowAdvance } from "../../../hooks/use-workflow";
import type { DetailSegment, DetailStep, NextAction } from "../../../hooks/use-workflow";
import { RailEmpty, RailHead, RailLoading } from "./RailPrimitives";
import type { RailBinding } from "./rail-binding";

/** "1.2s" · "4m" · "2h 14m" — how long a finished step took, from its own stamps. */
function took(startedIso: string | null, doneIso: string | null): string | null {
  if (!startedIso || !doneIso) return null;
  const ms = new Date(doneIso).getTime() - new Date(startedIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))}s`;
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

type StepState = "done" | "now" | "waiting" | "skipped" | "later";

/** The engine's `ready` set decides "now". A step is never "now" because of a name. */
function stepState(step: DetailStep, segment: DetailSegment, ready: Set<number>): StepState {
  if (step.completedAt || /done|complete/i.test(step.status)) return "done";
  if (/skip/i.test(step.status)) return "skipped";
  if (ready.has(step.stepEntryNo)) return "now";
  return segment.state === "open" ? "waiting" : "later";
}

/** The right-hand figure on a step row. Never a placeholder — absent when unknown. */
function stepFigure(step: DetailStep, state: StepState, startedAt: string | null): string | null {
  if (state === "done") return took(startedAt, step.completedAt) ?? (step.completedAt ? fmtDate(step.completedAt) : "done");
  if (step.dueDate) return fmtDate(step.dueDate);
  if (state === "now") return step.assigneeKind === "agent" ? "ready" : "you";
  return null;
}

/**
 * The DO-NEXT action, transferred from `WorkflowPanel` (R1). One primary button
 * carrying the step's OWN verb; a decision links to the full view instead, because
 * recording an outcome needs its outcomes in front of you.
 */
function StepAction({
  next,
  instanceNo,
  processHref,
}: {
  next: NextAction;
  instanceNo: number;
  processHref: string;
}) {
  const advance = useWorkflowAdvance();
  if (next.kind === "decision") {
    return (
      <Link className="railstep__cta" to={processHref} data-testid="rail-plan-cta">
        Decide →
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="railstep__cta"
      disabled={advance.isPending}
      onClick={() => advance.mutate({ instance_no: instanceNo, step_entry_no: next.stepEntryNo })}
      data-testid="rail-plan-cta"
    >
      {advance.isPending ? "…" : (next.verb || "Complete")}
    </button>
  );
}

export function RailPlan({ binding }: { binding: RailBinding }) {
  const { record } = binding;
  // №29 (S4.1) — the record's workflow is read ONLY when the record's plan is the one in
  // scope (see `planScope`). Passing `undefined` disables the query rather than skipping
  // the hook: an early return above a hook is the rules-of-hooks defect this lane has
  // already paid for twice, and it drops on exactly the branch nobody renders in dev.
  const planScope = binding.planScope;
  const anchorWf = useAnchorWorkflows(
    planScope === null ? undefined : record?.id,
    record?.type || "matter",
  );
  const instances = anchorWf.data?.instances ?? [];
  // The live one, else the most recent — the same choice the task cockpit's rail makes.
  const instance = instances.find((i) => !i.done) ?? instances[0];
  const detail = useInstanceDetail(instance?.instanceNo);
  const next = useWhatsNext(instance?.instanceNo);

  // №29 (S4.1) — the conversation is the subject and nothing is running for it. The record
  // this chat is anchored to may well have a live workflow; that is the RECORD's plan, not
  // this conversation's, and showing it here is a wrong answer rather than a fallback.
  if (planScope === null && record) {
    return (
      <RailEmpty>
        This conversation isn’t driving a run, so there are no steps to show. <b>{record.name}</b>
        {" "}has its own process — it is on the record, under Process.
      </RailEmpty>
    );
  }
  if (!record) {
    return (
      <RailEmpty>
        <b>{binding.label}</b> is not one piece of work — it is a place. Open a record, or click a
        run in the conversation, and its steps appear here.
      </RailEmpty>
    );
  }
  if (anchorWf.isLoading || (instance && detail.isLoading)) return <RailLoading />;
  if (!instance || !detail.data?.found) {
    return (
      <RailEmpty>
        No declared process is running on <b>{record.name}</b>. Work here is ad-hoc, so there are no
        steps to show — that is the honest state, not a missing panel.
      </RailEmpty>
    );
  }

  const segments = detail.data.segments ?? [];
  const nextAction = next.data?.nextAction;
  // Every step the engine will accept work on right now. `nextAction` is the LEAD
  // among them — the one that carries the button.
  const ready = new Set<number>((next.data?.ready ?? []).map((r) => r.stepEntryNo));
  if (nextAction && nextAction.kind !== "none") ready.add(nextAction.stepEntryNo);
  const leadStep = nextAction && nextAction.kind !== "none" ? nextAction.stepEntryNo : null;
  const totalSteps = segments.reduce((n, s) => n + s.steps.length, 0);
  const doneSteps = segments.reduce((n, s) => n + s.stepsDone, 0);
  const processHref = `/app/place/${record.type}/${record.id}/process`;

  return (
    <div className="railpane" data-testid="rail-plan">
      <RailHead>
        {detail.data.description || instance.templateCode} · {doneSteps} of {totalSteps} done
      </RailHead>
      <div className="railplan__meter" aria-hidden="true">
        <span style={{ flex: Math.max(doneSteps, 0) }} className="railplan__meterdone" />
        <span style={{ flex: Math.max(totalSteps - doneSteps, 0) }} />
      </div>

      {segments.map((seg) => (
        <div key={seg.segmentNo} className="railplan__seg" data-state={seg.state}>
          <div className="railplan__segname">{seg.name}</div>
          {seg.steps.map((step) => {
            const state = stepState(step, seg, ready);
            const figure = stepFigure(step, state, instance.startedAt ?? detail.data?.startedAt ?? null);
            const isLead = step.stepEntryNo === leadStep;
            return (
              <div key={step.stepEntryNo} className="railstep" data-state={state} data-testid="rail-plan-step">
                <span className="railstep__mark" aria-hidden="true">{state === "done" ? "✓" : ""}</span>
                <span className="railstep__name">{step.name}</span>
                {figure && <span className="railstep__t">{figure}</span>}
                {/* The lead ready step carries the action AND the engine's own reason
                    for it being the lead ("Ready now" / "Waiting on …"). */}
                {isLead && nextAction && (
                  <span className="railstep__do">
                    <span className="railstep__why">{nextAction.why}</span>
                    <StepAction next={nextAction} instanceNo={instance.instanceNo} processHref={processHref} />
                  </span>
                )}
              </div>
            );
          })}
          {seg.stateIssue && (
            <div className="railplan__issue" data-testid="rail-plan-issue">
              ⚠ {seg.stateIssue.message}
            </div>
          )}
        </div>
      ))}

      {nextAction && nextAction.kind !== "none" && nextAction.title && (
        <div className="railplan__next" data-testid="rail-plan-next">
          Completing <b>{nextAction.title}</b> moves this process on.
        </div>
      )}
      <Link className="raillink" to={processHref}>
        Open the full process →
      </Link>
    </div>
  );
}
